// src/shared/ui/ConfirmModal.jsx
//
// Generic confirm-before-action dialog. Started as
// connectors/components/DeactivateConfirmModal.jsx, then got promoted here
// once the admin catalogs (permissions, roles, users) needed the same
// dialog — those pages were calling bare `window.confirm("Deactivate this
// role?")`, which can't say what breaks and can't report why a failure
// happened. Every current caller confirms a deactivate, but nothing here is
// deactivate-specific: pass `title`/`confirmLabel` to reuse it for any other
// consequential action (e.g. "Delete", "Approve") instead of building
// another near-identical dialog later.
//
// It runs the async action itself and renders a rejection inline — success
// is the caller's cue to close (drop `open`), a failure keeps the dialog
// open with the reason, so a real API error (e.g. a 409 "still assigned to
// 3 users") has somewhere readable to land instead of the dialog just
// vanishing.
//
// Usage:
//   <ConfirmModal
//     open={Boolean(target)} entityLabel="permission" name={target?.name}
//     onClose={() => setTarget(null)} onConfirm={() => deactivate(target.id)}
//   >
//     Roles referencing it keep the row, but it stops granting access.
//   </ConfirmModal>

import { useState } from "react";
import PropTypes from "prop-types";
import { AlertTriangle, XCircle } from "lucide-react";
import Modal from "./Modal";
import Alert from "./Alert";
import Button from "./Button";

export default function ConfirmModal({
  open,
  title,
  entityLabel,
  name,
  children,
  confirmLabel = "Deactivate",
  variant = "danger",
  onClose,
  onConfirm,
}) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  function handleClose() {
    setError(null);
    onClose();
  }

  async function handleConfirm() {
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm();
    } catch (err) {
      setError(err?.message || `Could not ${confirmLabel.toLowerCase()} this ${entityLabel}.`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal open={open} title={title || `${confirmLabel} ${entityLabel}`} onClose={handleClose}>
      <div className="flex flex-col gap-4">
        <Alert variant="warning" icon={AlertTriangle} title={name ? `${confirmLabel} "${name}"?` : `${confirmLabel}?`}>
          {children}
        </Alert>

        {error && (
          <Alert variant="danger" icon={XCircle} title="That didn't work">
            {error}
          </Alert>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={handleClose} disabled={submitting}>
            Cancel
          </Button>
          <Button variant={variant} onClick={handleConfirm} loading={submitting}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

ConfirmModal.propTypes = {
  open: PropTypes.bool.isRequired,
  /** Overrides the Modal's own title; defaults to "{confirmLabel} {entityLabel}". */
  title: PropTypes.string,
  /** Lowercase singular noun for the default title, e.g. "permission" / "connection". */
  entityLabel: PropTypes.string.isRequired,
  /** The record's display name, quoted in the Alert headline. */
  name: PropTypes.string,
  /** What confirming actually does, in plain words. */
  children: PropTypes.node.isRequired,
  confirmLabel: PropTypes.string,
  variant: PropTypes.oneOf(["primary", "secondary", "ghost", "danger"]),
  onClose: PropTypes.func.isRequired,
  /** May return a promise; a rejection renders inline and keeps the dialog open. */
  onConfirm: PropTypes.func.isRequired,
};
